import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CodeDemo = () => {
  const [activeTab, setActiveTab] = useState('react');
  const [isRunning, setIsRunning] = useState(false);
  const [output, setOutput] = useState('');

  const codeExamples = {
    react: {
      title: 'React Component',
      language: 'jsx',
      code: `import React, { useState } from 'react';

const UserCard = ({ user }) => {
  const [isFollowing, setIsFollowing] = useState(false);

  const handleFollow = () => {
    setIsFollowing(!isFollowing);
    // API call would go here
  };

  return (
    <div className="p-4 border rounded-lg shadow-sm">
      <img 
        src={user.avatar} 
        alt={user.name}
        className="w-16 h-16 rounded-full mb-3"
      />
      <h3 className="font-semibold">{user.name}</h3>
      <p className="text-gray-600">{user.role}</p>
      <button
        onClick={handleFollow}
        className={\`mt-3 px-4 py-2 rounded \${
          isFollowing 
            ? 'bg-gray-200 text-gray-800' :'bg-blue-500 text-white'
        }\`}
      >
        {isFollowing ? 'Following' : 'Follow'}
      </button>
    </div>
  );
};

export default UserCard;`
    },
    node: {
      title: 'Node.js API',
      language: 'javascript',
      code: `const express = require('express');
const jwt = require('jsonwebtoken');
const bcrypt = require('bcrypt');

const app = express();
app.use(express.json());

// Authentication middleware
const authenticateToken = (req, res, next) => {
  const authHeader = req.headers['authorization'];
  const token = authHeader && authHeader.split(' ')[1];

  if (!token) {
    return res.sendStatus(401);
  }

  jwt.verify(token, process.env.JWT_SECRET, (err, user) => {
    if (err) return res.sendStatus(403);
    req.user = user;
    next();
  });
};

// Protected route
app.get('/api/profile', authenticateToken, (req, res) => {
  res.json({
    id: req.user.id,
    username: req.user.username,
    email: req.user.email,
    lastLogin: new Date().toISOString()
  });
});

app.listen(3000, () => {
  console.log('Server running on port 3000');
});`
    },
    python: {
      title: 'Python FastAPI',
      language: 'python',
      code: `from fastapi import FastAPI, HTTPException, Depends
from pydantic import BaseModel
from typing import List, Optional
import uvicorn

app = FastAPI(title="Manica Solutions API", version="1.0.0")

class User(BaseModel):
    id: int
    name: str
    email: str
    is_active: bool = True

class UserCreate(BaseModel):
    name: str
    email: str

# In-memory database for demo
users_db = []

@app.get("/")
async def root():
    return {"message": "Welcome to Manica Solutions API"}

@app.post("/users/", response_model=User)
async def create_user(user: UserCreate):
    user_id = len(users_db) + 1
    new_user = User(id=user_id, **user.dict())
    users_db.append(new_user)
    return new_user

@app.get("/users/", response_model=List[User])
async def get_users(skip: int = 0, limit: int = 10):
    return users_db[skip : skip + limit]

@app.get("/users/{user_id}", response_model=User)
async def get_user(user_id: int):
    for user in users_db:
        if user.id == user_id:
            return user
    raise HTTPException(status_code=404, detail="User not found")

if __name__ == "__main__":
    uvicorn.run(app, host="0.0.0.0", port=8000)`
    }
  };

  const runCode = () => {
    setIsRunning(true);
    setOutput('');
    
    // Simulate code execution
    setTimeout(() => {
      const outputs = {
        react: 'Component rendered successfully!\n✓ Props validated\n✓ State management working\n✓ Event handlers attached',
        node: 'Server started on port 3000\n✓ JWT middleware configured\n✓ Routes registered\n✓ Database connected',
        python: 'FastAPI server running on http://0.0.0.0:8000\n✓ API documentation available at /docs\n✓ Models validated\n✓ Endpoints active'
      };
      setOutput(outputs[activeTab]);
      setIsRunning(false);
    }, 2000);
  };

  return (
    <div className="bg-gray-900 rounded-brand overflow-hidden">
      {/* Code Editor Header */}
      <div className="flex items-center justify-between p-4 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center space-x-4">
          <div className="flex space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          <div className="flex space-x-1">
            {Object.entries(codeExamples).map(([key, example]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-3 py-1 text-sm rounded transition-brand ${
                  activeTab === key
                    ? 'bg-secondary text-white' :'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                {example.title}
              </button>
            ))}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName={isRunning ? 'Loader2' : 'Play'}
          iconPosition="left"
          onClick={runCode}
          disabled={isRunning}
          className={`text-green-400 hover:text-green-300 ${isRunning ? 'animate-spin' : ''}`}
        >
          {isRunning ? 'Running...' : 'Run Code'}
        </Button>
      </div>

      {/* Code Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Code Editor */}
        <div className="p-4 bg-gray-900">
          <pre className="text-sm text-gray-300 overflow-x-auto">
            <code className="font-mono leading-relaxed">
              {codeExamples[activeTab].code}
            </code>
          </pre>
        </div>

        {/* Output Panel */}
        <div className="p-4 bg-gray-800 border-l border-gray-700">
          <div className="flex items-center mb-3">
            <Icon name="Terminal" size={16} className="mr-2 text-green-400" />
            <span className="text-sm font-medium text-gray-300">Output</span>
          </div>
          <div className="bg-gray-900 rounded p-3 min-h-[200px]">
            {isRunning ? (
              <div className="flex items-center space-x-2 text-yellow-400">
                <Icon name="Loader2" size={16} className="animate-spin" />
                <span className="text-sm">Executing code...</span>
              </div>
            ) : output ? (
              <pre className="text-sm text-green-400 whitespace-pre-wrap">{output}</pre>
            ) : (
              <div className="text-gray-500 text-sm">Click "Run Code" to see output</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeDemo;